import { useState, useEffect } from 'react';
import { ShieldCheck, Key } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { errText } from '../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PinSetup - Dialog para configurar el PIN de firma electrónica
 * Props:
 *  - open, onClose, lang, onPinSet (callback)
 */
export default function PinSetup({ open, onClose, lang = 'es', onPinSet }) {
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const lb = lang === 'es'
    ? { title: 'Configurar PIN de Firma', desc: 'Establezca un PIN numerico de 4 a 6 digitos para firmar acciones criticas.', pin: 'PIN', confirm: 'Confirmar PIN', save: 'Guardar PIN', cancel: 'Cancelar', mismatch: 'Los PINs no coinciden', too_short: 'El PIN debe tener entre 4 y 6 digitos', success: 'PIN configurado correctamente' }
    : { title: 'Set Signature PIN', desc: 'Set a 4-6 digit numeric PIN to sign critical actions.', pin: 'PIN', confirm: 'Confirm PIN', save: 'Save PIN', cancel: 'Cancel', mismatch: 'PINs do not match', too_short: 'PIN must be 4-6 digits', success: 'PIN set successfully' };

  const handleSave = async () => {
    setError('');
    if (pin.length < 4 || pin.length > 6) { setError(lb.too_short); return; }
    if (pin !== pinConfirm) { setError(lb.mismatch); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/auth/pin`, { pin }, { withCredentials: true });
      toast.success(lb.success);
      setPin(''); setPinConfirm('');
      if (onPinSet) onPinSet();
      onClose();
    } catch (err) { setError(errText(err, 'Error')); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setPin(''); setPinConfirm(''); setError(''); onClose(); } }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key size={20} className="text-[#1f3e80]" />
            {lb.title}
          </DialogTitle>
          <DialogDescription>{lb.desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{lb.pin}</Label>
            <Input data-testid="pin-setup-input" type="password" inputMode="numeric" maxLength={6} value={pin}
              onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setError(''); }}
              placeholder="••••••" className="text-center text-lg tracking-[0.5em] font-mono" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label>{lb.confirm}</Label>
            <Input data-testid="pin-setup-confirm" type="password" inputMode="numeric" maxLength={6} value={pinConfirm}
              onChange={e => { setPinConfirm(e.target.value.replace(/\D/g, '')); setError(''); }}
              placeholder="••••••" className="text-center text-lg tracking-[0.5em] font-mono" />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{lb.cancel}</Button>
          <Button data-testid="pin-setup-save" onClick={handleSave} disabled={saving}
            className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-1">
            <ShieldCheck size={16} />{saving ? '...' : lb.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
